import React from 'react';
import { Dimensions, View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import Colors from '../../constants/Colors';
import PantryPhoto from '../pantry/PantryPhoto';
import timeUtils from '../../utils/timeUtils';
import Svg from '../Svg';
import { ServiceType, Visit } from '../../types';
import { useLocalization } from '../../store/utils/useLocalization';

type Props = {
  visit: Visit;
  onSelect: () => void;
  beforeToday: boolean;
};

function VisitListItem(props: Props) {
  const { visit, onSelect, beforeToday } = props;
  const { pantry } = visit;
  const { t } = useLocalization();

  const getServiceText = (visit: Visit) => {
    const returnData = { label: '', color: Colors.grayText };
    const time = timeUtils.timeSinceMidnightToString(visit.time);
    if (beforeToday) {
      // past, served/not served/did not show up
      // ${time} status
      if (visit.status === 3) {
        returnData.label = `${time}, ${t('visit_no_show')}`;
        returnData.color = Colors.brightRed;
      } else if (!visit.served_at) {
        returnData.label = `${time}, ${t('visit_not_served')}`;
        returnData.color = Colors.grayText;
      } else {
        returnData.label = `${time}, ${t('visit_served')}`;
      }
    } else {
      // today and future, group and time
      if (visit.served_at) {
        returnData.label = `${time}, ${t('visit_served')}`;
      } else if (visit.service_type === ServiceType.pre_registration) {
        // You're pre-registered, Come by between ${time} - ${until_time}
        const untilTime = timeUtils.timeSinceMidnightToString(visit.until_time);
        returnData.label = t('visit_pre_registered', {
          from_time: time,
          end_time: untilTime,
        });
        returnData.color = Colors.primaryColor;
      } else {
        // You are group ${group}, come by ${time}
        returnData.label = t('visit_group', {
          group_number: `${visit.group_id}`,
          time,
        });
        returnData.color = Colors.primaryColor;
      }
    }
    return returnData;
  };

  const serviceText = getServiceText(visit);
  const color = StyleSheet.create({
    label: {
      color: serviceText.color,
    },
  });
  const showAdditionalInfoNeeded =
    visit.status == 1 &&
    !beforeToday &&
    !pantry.additional_info_confirmed_at &&
    pantry.requested_fields?.length;

  return (
    <>
      <TouchableOpacity style={styles.container} onPress={onSelect}>
        <PantryPhoto url={pantry.small_photo_url} distance={pantry.distance} />
        <View style={styles.textContainer}>
          <Text style={styles.pantryDetailsName}>{pantry.name}</Text>
          <Text style={[styles.serviceText, color.label]}>{serviceText.label}</Text>
          {showAdditionalInfoNeeded ? (
            <View style={styles.warningTextContainer}>
              <Svg icon="unverified" color={Colors.brightRed} height={12} width={12} />
              <Text style={styles.warningText}>{t('tefap_additional_info_requested')}</Text>
            </View>
          ) : null}
        </View>
      </TouchableOpacity>
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    padding: 16,
  },
  textContainer: {
    paddingLeft: 16,
    width: Dimensions.get('window').width - 112,
  },
  pantryDetailsName: {
    flexWrap: 'wrap',
    fontFamily: 'roboto-regular',
  },
  serviceText: {
    fontSize: 12,
    color: Colors.grayText,
  },
  warningTextContainer: {
    paddingTop: 8,
    flexDirection: 'row',
  },
  warningText: {
    paddingLeft: 4,
    fontSize: 12,
    color: Colors.brightRed,
    fontWeight: '500',
  },
});

export default VisitListItem;
